import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  LocationsService,
  OrdersService,
  VehiclesService,
} from '@boma/supplier-portal/shared/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { fetch } from '@nx/angular';
import { map, withLatestFrom } from 'rxjs/operators';
import * as SalesActions from './sales.actions';

@Injectable()
export class SalesEffects {
  loadSales$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesActions.loadSales),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          const { sales } = state;
          return this.orderService
            .getSales({
              entityId: user.selectedOrganizationId,
              locationIds: sales?.locationIds,
              page: sales.nextPage,
              perPage: sales.perPage,
              query: sales.query,
              statuses: sales.statuses || sales.status,
              customerEntityIds: sales.customerIds,
              startDate: sales.dateRange?.fromDate,
              endDate: sales.dateRange?.toDate,
              orderType: sales.orderType,
            })
            .pipe(
              map((response) =>
                SalesActions.loadSalesSuccess({ payload: response })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return SalesActions.loadSalesFailure({ error });
        },
      })
    )
  );

  loadSale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesActions.loadSaleDetails),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .getOrder(user.selectedOrganizationId, action.id)
            .pipe(
              map((order) =>
                SalesActions.loadSaleDetailsSuccess({
                  order,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return SalesActions.loadSaleDetailsFailure({ error });
        },
      })
    )
  );

  updateSaleStatusToCancelled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesActions.updateSaleStatusToCancelled),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .updateOrderStatusToCancelled(
              user.selectedOrganizationId,
              action.id,
              action.comment,
              action.direction
            )
            .pipe(
              map((order) =>
                SalesActions.loadSaleDetailsSuccess({
                  order,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return SalesActions.loadSaleDetailsFailure({ error });
        },
      })
    )
  );

  loadLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesActions.loadSaleLocations),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;

          const values: any = action.defaultOption
            ? [action.defaultOption]
            : [];

          if (action.locationType === 'Physical') {
            return this.locationService
              .loadLocations(
                user.selectedOrganizationId,
                '1',
                '25',
                action.query
              )
              .pipe(
                map((response) =>
                  SalesActions.loadSaleLocationsSuccess({
                    payload: values.concat(response.items),
                  })
                )
              );
          } else {
            return this.vehicleService
              .loadVehicles(
                user.selectedOrganizationId,
                '1',
                '25',
                action.query
              )
              .pipe(
                map((response) =>
                  SalesActions.loadSaleLocationsSuccess({
                    payload: values.concat(response.items),
                  })
                )
              );
          }
        },

        onError: (action, error) => {
          console.error('Error', error);
          return SalesActions.loadSaleLocationsFailure({ error });
        },
      })
    )
  );

  loadOrderMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesActions.loadSaleMessages),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          const { sales } = state;
          return this.orderService
            .loadOrderMessages(
              user.selectedOrganizationId,
              action.id,
              action.direction
            )
            .pipe(
              map((messages) =>
                SalesActions.loadSaleMessagesSuccess({
                  payload: messages,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return SalesActions.loadSaleMessagesFailure({ error });
        },
      })
    )
  );

  createSaleMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SalesActions.createSaleMessage),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          const { sales } = state;
          return this.orderService
            .createOrderMessage(
              user.selectedOrganizationId,
              action.id,
              action.direction,
              action.content
            )
            .pipe(
              map((message) =>
                SalesActions.createSaleMessageSuccess({
                  message,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return SalesActions.createSaleMessageFailure({ error });
        },
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private orderService: OrdersService,
    private locationService: LocationsService,
    private vehicleService: VehiclesService,
    private router: Router
  ) {}
}
